import { useCallback } from 'react';
import { useAPIQuery } from 'shared/api/APIQuery';
import { requestJSONLoadboardAPI } from 'shared/api/LoadboardAPIClient';
import { useFlaggedSchema } from 'shared/hooks/useFlaggedSchema';
import {
  becomeVerifiedCarrier,
  BecomeVerifiedCarrierOptions,
} from 'shared/modules/loadboard/core/BecomVerifiedCarrier';
import {
  yupBoolean,
  yupEnum,
  yupObject,
  yupString,
} from 'shared/utils/YupUtils';
import { z } from 'zod';

export type LoadboardAccessDTO = z.infer<typeof loadboardAccessSchema>;

const loadboardAccessYupSchema = yupObject({
  carrier_guid: yupString(),
  is_in_private_network: yupBoolean(),
  access: yupEnum(['unlimited', 'limited'] as const),
  reported: yupBoolean(),
  verification_status: yupEnum([
    'not_submitted',
    'pending',
    'reviewed',
    'approved',
    'declined',
    'revoked',
    'non_verifiable',
  ]).defined(),
});

const loadboardAccessSchema = z.object({
  carrier_guid: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  is_in_private_network: z.boolean().nullable().default(false),
  access: z.enum(['unlimited', 'limited']).catch('unlimited'),
  reported: z.boolean().nullable().default(false),
  verification_status: z
    .enum([
      'not_submitted',
      'pending',
      'reviewed',
      'approved',
      'declined',
      'revoked',
      'non_verifiable',
    ])
    .catch('not_submitted'),
});

export function useLoadboardAccess() {
  const schema = useFlaggedSchema(
    'ctms_loadboard_zod_schema',
    loadboardAccessYupSchema,
    loadboardAccessSchema,
  );

  return useAPIQuery<LoadboardAccessDTO>(
    ['loadboard', 'access'],
    () => requestJSONLoadboardAPI('GET /internal/v3/loadboard/access'),
    { schema, staleTime: Infinity },
  );
}

export function useBecomeVerifiedCarrierNavigation() {
  const { data: access } = useLoadboardAccess();

  return useCallback(
    (options: BecomeVerifiedCarrierOptions) => {
      becomeVerifiedCarrier(access?.verification_status, { ...options });
    },
    [access],
  );
}
